import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import type { ContentItem, CommitteeMember, Testimonial } from '../../types/content';

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200';

interface MissionVideo {
  type?: 'youtube' | 'video';
  url: string;
  fallbackImage: string;
  autoplay?: boolean;
  controls?: boolean;
  modestBranding?: boolean;
  showRelated?: boolean;
}

interface PageContent {
  title: string;
  description: string;
  missionStatement: string;
  missionVideo: MissionVideo;
  goals: Array<{ title: string; description: string }>;
  committeeMembers: CommitteeMember[];
  testimonials: Testimonial[];
}

const defaultContent: PageContent = {
  title: 'About The Tournament',
  description: 'Celebrating Excellence in African Golf Since 2020',
  missionStatement: '',
  missionVideo: { url: '', fallbackImage: '' },
  goals: [],
  committeeMembers: [],
  testimonials: [],
};

export default function MobileAbout() {
  const [pageContent, setPageContent] = useState<PageContent>(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data: sections, error } = await supabase
        .from('content_sections')
        .select('id, title, content_items(*)')
        .eq('page', 'about');

      if (error) throw error;

      if (sections) {
        const headerSection = sections.find(s => s.title === 'Page Header');
        const missionSection = sections.find(s => s.title === 'Mission');
        const committeeSection = sections.find(s => s.title === 'Committee');
        const testimonialsSection = sections.find(s => s.title === 'Testimonials');

        const getItem = (section: any, title: string): ContentItem | undefined =>
          section?.content_items?.find((item: ContentItem) => item.title === title && item.status === 'Published');

        const parse = (item: ContentItem | undefined) => {
          if (!item?.content) return null;
          try {
            return typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
          } catch {
            return item.content;
          }
        };

        const parsedGoals = parse(getItem(missionSection, 'Goals'));
        const parsedCommittee = parse(getItem(committeeSection, 'Committee Members'));
        const parsedTestimonials = parse(getItem(testimonialsSection, 'Testimonials List'));

        setPageContent(prev => ({
          title: parse(getItem(headerSection, 'Title')) || prev.title,
          description: parse(getItem(headerSection, 'Description')) || prev.description,
          missionStatement: parse(getItem(missionSection, 'Mission Statement')) || '',
          missionVideo: parse(getItem(missionSection, 'Video Content')) || prev.missionVideo,
          goals: Array.isArray(parsedGoals) ? parsedGoals : [],
          committeeMembers: Array.isArray(parsedCommittee) ? parsedCommittee : [],
          testimonials: Array.isArray(parsedTestimonials) ? parsedTestimonials : [],
        }));
      }
    } catch (error) {
      console.error('Error fetching about content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  const hasGoals = pageContent.goals.length > 0;
  const hasMissionContent = pageContent.missionStatement || hasGoals || pageContent.missionVideo.url;
  const hasCommittee = pageContent.committeeMembers.length > 0;
  const hasTestimonials = pageContent.testimonials.length > 0;
  const heroImage = pageContent.missionVideo.fallbackImage || DEFAULT_IMAGE;

  return (
    <div className="pb-10">
      {/* Hero */}
      <div className="relative h-52 overflow-hidden">
        <img
          src={heroImage}
          alt="Tournament"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/70" />
        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="text-white font-display font-bold text-2xl leading-tight">{pageContent.title}</h1>
          {pageContent.description && (
            <p className="text-golf-sand text-sm mt-1">{pageContent.description}</p>
          )}
        </div>
      </div>

      {/* Mission */}
      {hasMissionContent && (
        <div className="px-4 pt-5">
          <h2 className="font-display font-bold text-golf-gray text-lg mb-3">Our Mission</h2>

          {pageContent.missionVideo.type === 'youtube' && pageContent.missionVideo.url ? (
            <div className="rounded-2xl overflow-hidden mb-4">
              <iframe
                src={`https://www.youtube.com/embed/${pageContent.missionVideo.url}?autoplay=0&controls=1&modestbranding=1&rel=0`}
                title="Mission Video"
                className="w-full aspect-video border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : pageContent.missionVideo.fallbackImage ? (
            <div className="rounded-2xl overflow-hidden mb-4">
              <img
                src={pageContent.missionVideo.fallbackImage}
                alt="Our Mission"
                className="w-full aspect-video object-cover"
              />
            </div>
          ) : null}

          {pageContent.missionStatement && (
            <p className="text-gray-600 text-sm leading-relaxed mb-4">{pageContent.missionStatement}</p>
          )}

          {hasGoals && (
            <div className="flex flex-col gap-3 mb-5">
              {pageContent.goals.map((goal, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-golf-orange rounded-full flex-shrink-0 mt-2" />
                  <p className="text-sm text-gray-600 leading-relaxed">{goal.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Committee */}
      {hasCommittee && (
        <div className="px-4 pt-2 pb-2">
          <h2 className="font-display font-bold text-golf-gray text-lg mb-4">Planning Committee</h2>
          <div className="flex flex-col gap-4">
            {pageContent.committeeMembers.map((member, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex gap-4 items-start">
                {member.image && (
                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-golf-gray text-sm">{member.name}</p>
                  <p className="text-golf-orange text-xs font-medium mb-1">{member.title}</p>
                  {member.bio && <p className="text-xs text-gray-500 leading-relaxed">{member.bio}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Testimonials */}
      {hasTestimonials && (
        <div className="mx-4 mt-5 bg-golf-gray rounded-2xl p-4">
          <h2 className="font-display font-bold text-white text-lg mb-4">What People Say</h2>
          <div className="flex flex-col gap-4">
            {pageContent.testimonials.map((testimonial, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  {testimonial.image && (
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={testimonial.image} alt={testimonial.author} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-white text-sm">{testimonial.author}</p>
                    <p className="text-golf-orange text-xs">{testimonial.title}</p>
                  </div>
                </div>
                <blockquote className="text-golf-sand text-xs leading-relaxed">"{testimonial.quote}"</blockquote>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
